import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import {
  Grid,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { Info as InfoIcon, Edit as EditIcon } from '@mui/icons-material';

import InputDataSection from './InputDataSection';
import OutputDataSection from './OutputDataSection';
import DatasetDrawer from '@/components/shared/DatasetDrawer';
import VolumeDrawer from '@/components/shared/VolumeDrawer';
import DataConnectorsField from './DataConnectorsField';
import PrivateServicesField from './PrivateServicesField';
import JOB_TYPES from '@/components/jobs/types';
import cleanEmptyValues from '@/util/cleanEmptyValues';

export default function DataFields({
  initialValues,
  type,
  onChange,
  edit = false,
}) {
  const [values, setValues] = useState({
    input_uri: '',
    output_uri: '',
    ...initialValues,
    datasets: Array.isArray(initialValues?.datasets)
      ? initialValues.datasets
      : [],
    volumes: Array.isArray(initialValues?.volumes)
      ? initialValues.volumes
      : [],
    data_connectors: Array.isArray(initialValues?.data_connectors)
      ? initialValues.data_connectors
      : [],
    private_services: Array.isArray(initialValues?.private_services)
      ? initialValues.private_services
      : [],
  });

  const [errors, setErrors] = useState({
    input_uri_error: false,
    output_uri_error: false,
    input_endpoint_error: false,
    output_endpoint_error: false,
    interval_error: false,
    dataset_errors:
      initialValues && initialValues.datasets
        ? new Array(initialValues.datasets.length).fill(false)
        : [],
    volume_errors:
      initialValues && initialValues.volumes
        ? new Array(initialValues.volumes.length).fill(false)
        : [],
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [volumeDrawerOpen, setVolumeDrawerOpen] = useState(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const cleanedData = cleanEmptyValues(values);
      onChange('data', cleanedData || {}, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const datasets_filtered = values.datasets
      .map((dataset) => (!dataset.type ? undefined : dataset))
      .filter((i) => i);

    const volumes_filtered = values.volumes
      .map((volume) => (!volume.volume_id && !volume.id ? undefined : volume))
      .filter((i) => i);

    const dataset_error = new Set(errors.dataset_errors);
    const volume_error = new Set(errors.volume_errors);
    const dataValues = { ...values, datasets: datasets_filtered, volumes: volumes_filtered };
    const cleanedData = cleanEmptyValues(dataValues);
    onChange(
      'data',
      cleanedData || {},
      errors.input_uri_error ||
        errors.output_uri_error ||
        errors.input_endpoint_error ||
        errors.output_endpoint_error ||
        errors.interval_error ||
        dataset_error.has(true) ||
        volume_error.has(true),
    );
  }, [values, errors, onChange]);

  const datasetErrorCheck = (datasets) => {
    const names = datasets.map((dataset) => {
      if (dataset.name) {
        return dataset.name.toLowerCase().replace(' ', '_');
      }
      return undefined;
    });
    const dup_names = names.filter((e, i, a) => a.indexOf(e) !== i);
    const errors = datasets.map((dataset) => {
      if (dataset.name) {
        return dup_names.includes(dataset.name.toLowerCase().replace(' ', '_'));
      }
      return true;
    });
    return errors;
  };

  const volumeErrorCheck = (volumes) => {
    const names = volumes.map((volume) => {
      if (volume.name) {
        return volume.name.toLowerCase().replace(' ', '_');
      }
      return undefined;
    });
    const dup_names = names.filter((e, i, a) => a.indexOf(e) !== i);
    const errors = volumes.map((volume) => {
      if (volume.name) {
        return dup_names.includes(volume.name.toLowerCase().replace(' ', '_'));
      }
      return true;
    });
    return errors;
  };

  const setChanged = () => {
    // This is a no-op but kept for compatibility with field components
  };

  return (
    <Grid container spacing={2}>
      {type === JOB_TYPES.INFERENCE.value ? (
        <InputDataSection
          values={values}
          errors={errors}
          setValues={setValues}
          setErrors={setErrors}
          setChanged={setChanged}
          edit={edit}
        />
      ) : [JOB_TYPES.TRAINING.value, JOB_TYPES.NOTEBOOK.value].includes(
          type,
        ) ? (
        <>
          <Grid size={12}>
            <Typography variant='h6'>Datasets</Typography>
          </Grid>
          <Grid size={12}>
            <Button
              variant='outlined'
              startIcon={<EditIcon />}
              onClick={() => setDrawerOpen(true)}
            >
              {values.datasets && values.datasets.length > 0
                ? `Manage Datasets (${values.datasets.length})`
                : 'Add Datasets'}
            </Button>
          </Grid>
          <DatasetDrawer
            open={drawerOpen}
            handleClose={() => setDrawerOpen(false)}
            initialValues={values.datasets || []}
            setValue={(newDatasets) => {
              const dataset_errors = datasetErrorCheck(newDatasets);
              setValues({
                ...values,
                datasets: newDatasets,
              });
              setErrors({
                ...errors,
                dataset_errors,
              });
            }}
          />
        </>
      ) : undefined}

      {[JOB_TYPES.TRAINING.value, JOB_TYPES.INFERENCE.value].includes(
        type,
      ) ? (
        <OutputDataSection
          values={values}
          errors={errors}
          setValues={setValues}
          setErrors={setErrors}
          setChanged={setChanged}
          edit={edit}
          type={type}
        />
      ) : undefined}

      {type === JOB_TYPES.TRAINING.value ? (
        <Grid size={12}>
          <Alert
            severity='info'
            icon={<InfoIcon />}
            action={
              <Link
                to='/account/settings'
                target='_blank'
                style={{ textDecoration: 'none' }}
              >
                <Typography variant='body2' color='primary'>
                  Add Third Party Keys Here
                </Typography>
              </Link>
            }
          >
            Storage Type Disabled?
          </Alert>
        </Grid>
      ) : undefined}
      {[
        JOB_TYPES.ENDPOINT.value,
        JOB_TYPES.LLM.value,
        JOB_TYPES.NOTEBOOK.value,
      ].includes(type) ? (
        <>
          <Grid size={12}>
            <Typography variant='h6'>Volumes</Typography>
          </Grid>
          <Grid size={12}>
            <Button
              variant='outlined'
              startIcon={<EditIcon />}
              onClick={() => setVolumeDrawerOpen(true)}
            >
              {values.volumes && values.volumes.length > 0
                ? `Manage Volumes (${values.volumes.length})`
                : 'Add Volumes'}
            </Button>
          </Grid>
          <VolumeDrawer
            open={volumeDrawerOpen}
            handleClose={() => setVolumeDrawerOpen(false)}
            initialValues={values.volumes || []}
            setValue={(newVolumes) => {
              const volume_errors = volumeErrorCheck(newVolumes);
              setValues({
                ...values,
                volumes: newVolumes,
              });
              setErrors({
                ...errors,
                volume_errors,
              });
            }}
          />
        </>
      ) : undefined}
      <Grid size={12}>
        <Typography variant='h6'>Network Resources</Typography>
      </Grid>
      <Grid size={12}>
        <DataConnectorsField
          value={values.data_connectors || []}
          setValue={(newValue) => {
            setValues({
              ...values,
              data_connectors: newValue,
            });
          }}
          setChanged={setChanged}
          edit={edit}
        />
      </Grid>
      <Grid size={12}>
        <PrivateServicesField
          value={values.private_services || []}
          setValue={(newValue) => {
            setValues({
              ...values,
              private_services: newValue,
            });
          }}
          setChanged={setChanged}
          edit={edit}
        />
      </Grid>
    </Grid>
  );
}
